import { useCallback, useEffect, useRef, useState } from 'react';
import { MapContainer, Marker, Polyline, TileLayer } from 'react-leaflet';
import L from 'leaflet';
import * as turf from '@turf/turf';
import CONFIG from '../config';
import TopHeaderMenu from '../components/TopHeaderMenu';
import CompeticionHeaderMenu from '../components/CompeticionHeaderMenu';
import { useNavigate, useParams } from 'react-router-dom';
import { calculaPrevision } from '../utils/time';
import { applyDelta } from '../utils/wsDelta';

const iconoSalida = new L.Icon({
  iconUrl: '/img/ico-salida-v2.png', iconSize: [22, 35], iconAnchor: [11, 35],
  popupAnchor: null, shadowUrl: null, shadowSize: null, shadowAnchor: null
});
const iconoMeta = new L.Icon({
  iconUrl: '/img/ico-meta-v2.png', iconSize: [22, 35], iconAnchor: [11, 35],
  popupAnchor: null, shadowUrl: null, shadowSize: null, shadowAnchor: null
});

function crearIconoAtleta(dorsal, finalizado) {
  return new L.DivIcon({
    className: '',
    html: `<div style="
      background:${finalizado ? '#22c55e' : '#d42c19'};
      color:white;font-size:10px;font-weight:bold;
      border-radius:50%;width:28px;height:28px;
      display:flex;align-items:center;justify-content:center;
      border:2px solid white;box-shadow:0 1px 4px rgba(0,0,0,0.5);
      font-family:monospace;">
      ${dorsal}
    </div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });
}

function CompeticionGps() {
  const lineRef = useRef(null);
  const mapRef = useRef(null);
  const wsRef = useRef(null);
  const wsReconnectRef = useRef(null);
  const intervalRef = useRef(null);

  const [evento, setEvento] = useState(null);
  const [lineCoords, setLineCoords] = useState([]);
  const [competicion, setCompeticion] = useState(null);
  const [controles, setControles] = useState([]);
  const [sectores, setSectores] = useState([]);
  const [allResultados, setAllResultados] = useState([]);
  const [atletaPositions, setAtletaPositions] = useState([]);

  // refs para acceder en callbacks sin dependencias estale
  const stateRef = useRef({ competicion: null, controles: [], sectores: [], allResultados: [], lineCoords: [] });

  const navigate = useNavigate();
  const params = useParams();

  function onClickBack() {
    navigate(`/competicion/${params.competicionId}`);
  }

  // Ajustar bounds cuando cambia el recorrido
  useEffect(() => {
    if (mapRef.current && lineRef.current && lineCoords.length > 0) {
      mapRef.current.fitBounds(lineRef.current.getBounds());
    }
  }, [lineCoords]);

  // Calcular posiciones estimadas de atletas
  const calcularPosiciones = useCallback(() => {
    const { competicion: comp, controles: ctrl, sectores: sect, allResultados: results, lineCoords: coords } = stateRef.current;
    if (!comp || !ctrl.length || !coords.length || !results.length) return;

    const ultimoControl = ctrl[ctrl.length - 1];
    if (!ultimoControl || !ultimoControl.distancia) return;

    const turfLine = turf.lineString(coords);
    const totalLongKm = turf.length(turfLine, { units: 'kilometers' });

    const positions = [];
    for (const resultado of results) {
      const prev = calculaPrevision(comp, ctrl, sect, resultado);
      if (!prev || prev.distancia === null || prev.inicioDistancia === null) continue;

      const distMetros = prev.inicioDistancia + prev.distancia;
      if (distMetros <= 0) continue;

      const fraccion = Math.min(distMetros / ultimoControl.distancia, 1);
      const distKm = fraccion * totalLongKm;

      try {
        const point = turf.along(turfLine, distKm, { units: 'kilometers' });
        const [lng, lat] = point.geometry.coordinates;
        const metaControlId = ultimoControl.id;
        const finalizado = !!resultado[`control_${metaControlId}_puesto`];
        positions.push({ dorsal: resultado.dorsal, lat, lng, finalizado });
      } catch (e) { /* skip */ }
    }
    setAtletaPositions(positions);
  }, []);

  // Actualizar ref cuando cambia el estado
  useEffect(() => { stateRef.current.competicion = competicion; }, [competicion]);
  useEffect(() => { stateRef.current.controles = controles; }, [controles]);
  useEffect(() => { stateRef.current.sectores = sectores; }, [sectores]);
  useEffect(() => { stateRef.current.allResultados = allResultados; calcularPosiciones(); }, [allResultados, calcularPosiciones]);
  useEffect(() => { stateRef.current.lineCoords = lineCoords; calcularPosiciones(); }, [lineCoords, calcularPosiciones]);

  // Cargar datos iniciales
  useEffect(() => {
    if (!params.competicionId) return;
    fetch(`${CONFIG.API_URL}/api/1.0/live/${params.competicionId}/init/`)
      .then(r => r.json())
      .then(data => {
        const comp = data.competicion;
        setCompeticion(comp);
        setEvento(data.evento);
        setSectores(data.sectores || []);
        setControles(data.controles || []);
        if (comp.mapa_geojson) {
          try {
            const geoJson = JSON.parse(comp.mapa_geojson);
            setLineCoords(geoJson.features[0].geometry.coordinates);
          } catch (e) { console.error('mapa_geojson parse error:', e); }
        }
      })
      .catch(err => console.error('live-init error:', err));
  }, [params.competicionId]);

  // WebSocket
  useEffect(() => {
    if (!params.competicionId) return;

    const conectar = () => {
      const ws = new WebSocket(`${CONFIG.WS_URL}/ws/${params.competicionId}`);
      wsRef.current = ws;

      ws.onmessage = (event) => {
        const data = JSON.parse(event.data);
        if (data.tipo === 'update') {
          const newResultados = (data.resultados || []).map(r => ({ ...r, ...r.controles }));
          setAllResultados(newResultados);
          if (data.controles && data.controles.length) setControles(data.controles);
        } else if (data.tipo === 'delta') {
          setAllResultados(prev => applyDelta(prev, data.resultados || [], data.eliminados || []));
          if (data.controles && data.controles.length) setControles(data.controles);
        }
      };

      ws.onclose = () => {
        wsReconnectRef.current = setTimeout(conectar, 3000);
      };
    };

    conectar();

    // Recalcular posiciones cada 10s aunque no lleguen datos nuevos
    intervalRef.current = setInterval(calcularPosiciones, 10000);

    return () => {
      if (wsRef.current) wsRef.current.close();
      if (wsReconnectRef.current) clearTimeout(wsReconnectRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [params.competicionId, calcularPosiciones]);

  return (
    <div style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0 }}>
      <TopHeaderMenu onClickBack={onClickBack} competicion={competicion} evento={evento} />
      <CompeticionHeaderMenu competicion={competicion} selected={5} evento={evento} />
      <MapContainer
        ref={mapRef}
        boundsOptions={{ padding: [0, 0] }}
        style={{ position: 'absolute', top: '176px', margin: 0, bottom: 0, padding: 0 }}
        center={[51.505, -0.09]}
        zoom={13}
        scrollWheelZoom={false}
      >
        <TileLayer url={CONFIG.MAP_LAYER} />
        <Polyline
          ref={lineRef}
          color={CONFIG.PRIMARY_COLOR}
          positions={lineCoords.map(r => [r[1], r[0]])}
        />
        {lineCoords.length > 0 && (
          <>
            <Marker position={[lineCoords[0][1], lineCoords[0][0]]} icon={iconoSalida} />
            <Marker position={[lineCoords[lineCoords.length - 1][1], lineCoords[lineCoords.length - 1][0]]} icon={iconoMeta} />
          </>
        )}
        {atletaPositions.map(a => (
          <Marker
            key={a.dorsal}
            position={[a.lat, a.lng]}
            icon={crearIconoAtleta(a.dorsal, a.finalizado)}
          />
        ))}
      </MapContainer>
    </div>
  );
}

export default CompeticionGps;
